import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight, Clock, Heart } from "lucide-react";
import Logo from "@/components/Logo";
import { SITE } from "@/lib/site";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative mt-10 md:mt-14 text-white bg-gradient-to-br from-[hsl(200_55%_14%)] via-primary to-[hsl(155_55%_22%)] overflow-hidden"
    >
      {/* decorative orbs */}
      <div className="glow-orb -top-32 -left-20 w-[420px] h-[420px] bg-white/10 opacity-40" />
      <div className="glow-orb -bottom-32 -right-20 w-[420px] h-[420px] bg-accent/30 opacity-50" />

      <div className="container mx-auto pt-10 md:pt-14 pb-8 md:pb-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="rounded-2xl bg-white/95 p-1 shadow-md ring-1 ring-white/30">
                <Logo variant="mark" tone="brand" size={44} />
              </span>
              <Logo variant="wordmark" wordmarkTone="onBrand" wordmarkSize="lg" />
            </div>
            <p className="text-white/80 text-pretty leading-relaxed max-w-md">
              Compassionate psychiatric care, in person & via telehealth across {SITE.state}.
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-portal"
              className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-white text-primary text-sm font-semibold hover:bg-white/90 transition-colors shadow-md"
            >
              Schedule Appointment
            </Link>
          </div>

          {/* Sitemap */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.22em] text-white/60 mb-5">
              Explore
            </h4>
            <ul className="space-y-3">
              {[
                { to: "/about", label: "About Us" },
                { to: "/services", label: "Services" },
                { to: "/philosophy", label: "Philosophy of Care" },
                { to: "/contact", label: "Contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-white/85 hover:text-white inline-flex items-center gap-1.5 transition-colors"
                  >
                    {l.label}
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.22em] text-white/60 mb-5">
              Reach Us
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-white mt-0.5 shrink-0" />
                <a href={`tel:${SITE.phoneRaw}`} data-testid="footer-phone" className="text-white hover:text-white/80 transition-colors">
                  {SITE.phone} <span className="text-white/60">· {SITE.ext}</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-white mt-0.5 shrink-0" />
                <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="text-white hover:text-white/80 transition-colors break-all">
                  {SITE.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-white mt-0.5 shrink-0" />
                <span className="text-white/90">{SITE.address}</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-white mt-0.5 shrink-0" />
                <span className="text-white/90">{SITE.hours}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 md:mt-14 pt-6 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/70">
          <p>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <span className="opacity-40">·</span>
            <Link to="/terms" data-testid="footer-link-terms" className="hover:text-white transition-colors">
              Terms &amp; Conditions
            </Link>
            <span className="opacity-40">·</span>
            <span className="font-display italic text-sm flex items-center gap-2">
              <Heart className="w-3.5 h-3.5 text-white/80" /> Care that listens.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
